/**
 * Modal dialog for creating and editing company details including branding and call-type settings.
 *
 * @module components/admin/EditCompanyModal
 * @author Snowfly
 * @version 1.0.0
 */
import {
    Box, DialogTitle, FormControlLabel, Grid, InputLabel, Tabs, TextField
} from "@material-ui/core";
import DialogContent from "@material-ui/core/DialogContent";
import Button from "@material-ui/core/Button";
import Switch from "@material-ui/core/Switch";
import DialogActions from "@material-ui/core/DialogActions";
import Dialog from "@material-ui/core/Dialog";
import React, {useEffect} from "react";
import PropTypes from "prop-types";
import {connect} from "react-redux";
import {getPresignedUrl, uploadToPresignedUrl} from "../../services/result.service";
import CloseIcon from "@material-ui/icons/Close";
import Tab from "@material-ui/core/Tab";
import TabPanel from "@material-ui/lab/TabPanel";
import TabContext from "@material-ui/lab/TabContext";
import Paper from "@material-ui/core/Paper";
import TableContainer from "@material-ui/core/TableContainer";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableBody from "@material-ui/core/TableBody";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import AddIcon from "@material-ui/icons/Add";
import Tooltip from "@material-ui/core/Tooltip";

/**
 * EditCompanyModal - A React component for displaying a modal that allows users to edit company
 * details.
 *
 * This component serves as an interface for editing a company's attributes, such as name, domain,
 * logo, splash image, and other associated details. It provides handlers and state management to
 * facilitate user interaction, data validation, and synchronization between the component and
 * parent state. The modal's visibility and actions, including saving or closing, are controlled by
 * the provided props and internal logic.
 *
 * @component
 * @param {Object} props - The props passed to the EditCompanyModal component.
 * @param {Object} props.company - The company object to be edited. Contains properties such as
 *     name, domain, owner information, and associated resources like products and call types.
 * @param {Function} props.setCompany - A state update function to synchronize changes to the
 *     `company` object with the parent component.
 * @param {boolean} props.visible - Determines the visibility of the modal. If `true`, the modal is
 *     rendered; otherwise, it is hidden.
 * @param {Function} props.onClose - A callback function triggered when the modal is closed without
 *     saving changes.
 * @param {Function} props.onSave - A callback function triggered to save the current changes in
 *     the modal. If the function returns `false`, saving is not finalized.
 * @param {Object} props.classes - An optional object used to provide custom styling through class
 *     name mappings.
 */
const EditCompanyModal = ({
    company,
    setCompany,
    visible,
    onClose,
    onSave,
    classes,
}) => {
    const MAX_LOGO_SIZE = 1024 * 1024;
    const MAX_SPLASH_SIZE = 1024 * 1024 * 4;

    const [isModalVisible, setIsModalVisible] = React.useState(false);
    const [isActiveCompany, setIsActiveCompany] = React.useState(true);
    const [currentCompany, setCurrentCompany] = React.useState({});
    const [logoHover, setLogoHover] = React.useState(false);
    const [splashHover, setSplashHover] = React.useState(false);
    const [curTab, setCurTab] = React.useState(0);

    useEffect(
        () => {
            setIsModalVisible(visible);
        },
        [visible]
    );

    useEffect(
        () => {
            console.log('Editting Company: %o', company);
            if (!company.owner) {
                company.owner = {
                    firstName: '',
                    lastName: '',
                    email: '',
                    password: '',
                };
            }
            if (!company.outsourcers) {
                company.outsourcers = [];
            }
            if (!company.products) {
                company.products = [];
            }
            if (!company.callTypes) {
                company.callTypes = [];
            }
            setCurrentCompany(company);
            setIsActiveCompany(company?.active);
        },
        [company]
    );

    /**
     * Closes the modal and performs associated cleanup actions.
     *
     * The `handleClose` function is responsible for invoking the `onClose` callback
     * if it is provided, hiding the modal, and resetting the current tab to its
     * initial state.
     *
     * @callback handleClose
     */
    const handleClose = (/* evt */) => {
        if (onClose) {
            onClose();
        }
        setIsModalVisible(false);
        setCurTab(0);
    };

    /**
     * Handles the save operation, optionally invoking provided save and close callbacks.
     *
     * If the `onSave` callback is provided, it will be awaited, and if it resolves to `false`,
     * the operation will terminate early. If the `onClose` callback is provided, it will
     * be executed after the successful execution of `onSave`. The method concludes by
     * updating the state to hide the modal and reset the current tab to the first one.
     *
     * @return {Promise<void>} A Promise that resolves when all actions are completed.
     */
    const handleSave = async (/* evt */) => {
        if (onSave) {
            if (!await onSave()) {
                return;
            }
        }
        if (onClose) {
            await onClose();
        }
        setIsModalVisible(false);
        setCurTab(0);
    }

    /**
     * Handles the closing of a dialog or modal.
     *
     * Depending on the triggering element's ID, this function performs specific actions:
     * - If the triggering element has an ID of 'sendButton' and the `onSave` callback is defined,
     * it calls the `onSave` method.
     * - Irrespective of the triggering element, if the `onClose` callback is defined, it calls the
     * `onClose` method.
     *
     * Additionally, it sets the visibility of the modal to `false`.
     *
     * @param {Event} evt - The event object triggered by the user interaction.
     */
    const doCloseDialog = (evt) => {
        if (evt.currentTarget.id === 'sendButton') {
            if (onSave) {
                onSave();
            }
        }
        if (onClose) {
            onClose();
        }
        setIsModalVisible(false);
        setCurTab(0);
    };

    /**
     * Updates the `name` property of both `currentCompany` and `company`
     * objects with the value from the event target.
     *
     * @function handleNameChange
     * @param {Object} evt - The event object containing the new value for the `name` property.
     * @param {Object} evt.target - The target element from which the value is extracted.
     * @param {string} evt.target.value - The new value to be set for the `name` property.
     */
    const handleNameChange = (evt) => {
        setCurrentCompany({...currentCompany, name: evt.target.value});
        setCompany({...currentCompany, name: evt.target.value});
    };

    /**
     * Handles the domain change event for a company object.
     *
     * Updates the `domain` property of the `currentCompany` object and synchronizes
     * it with the `setCurrentCompany` and `setCompany` state functions. The updated
     * value is retrieved from the event target's value property.
     *
     * @param {Object} evt - The event object triggered by an input field.
     * @param {string} evt.target.value - The new domain value entered by the user.
     */
    const handleDomainChange = (evt) => {
        setCurrentCompany({...currentCompany, domain: evt.target.value});
        setCompany({...currentCompany, domain: evt.target.value});
    };

    /**
     * Asynchronous function to handle the change of a company logo.
     * This function processes the file selected by the user, validates its size,
     * retrieves a pre-signed URL for upload, uploads the logo to the server,
     * and updates the company information with the new logo URL if the upload is successful.
     *
     * @param {Event} evt - The event triggered by the file input change, containing the selected
     *     file and event metadata.
     */
    const handleLogoChange = async (evt) => {
        const file = evt.target.files[0],
            fileName = file.name;

        if (file.size > MAX_LOGO_SIZE) {
            alert('Logo size exceeds more than ' + (MAX_LOGO_SIZE / 1024) + 'MB');
            return;
        }

        //setLinearLogoLoader(true);
        let error = "There was an error Uploading the Company Logo!";

        try {
            let url = await getPresignedUrl('logo', fileName);
            if (url && url.success) {
                // Upload to presigned upload url
                let result = await uploadToPresignedUrl(
                    url.presignedUrl,
                    file
                );

                if (result && result.success) {
                    setCurrentCompany(
                        {...currentCompany, logoUrl: url.downloadUrl}
                    );
                    setCompany({...currentCompany, logoUrl: url.downloadUrl});
                    error = null;
                }
            }
        } catch (e) {
            error = e.toString();
        }

        if (error) {
            alert(error);
        }
    };

    /**
     * Removes the logo URL for the current company.
     *
     * This function updates the `currentCompany` and `company` objects by setting
     * the `logoUrl` property to an empty string. It helps to clear any existing
     * logo URL data for the company.
     */
    const removeLogoUrl = () => {
        setCurrentCompany(
            {...currentCompany, logoUrl: ''}
        );
        setCompany(
            {...currentCompany, logoUrl: ''}
        );
    };

    /**
     * Asynchronous function to handle changes in the splash image input field.
     * This function processes the uploaded file by validating its size, obtaining
     * a presigned URL for the upload, and updating the company's splash image if
     * the file upload is successful.
     *
     * @param {Event} evt - The input change event triggered by the file upload field.
     * @throws Will display an alert if the uploaded file exceeds the maximum permissible size
     *     (MAX_SPLASH_SIZE).
     * @throws Will handle unexpected errors and update the error message to indicate an issue
     *     during upload.
     */
    const handleSplashChange = async (evt) => {
        const file = evt.target.files[0],
            fileName = file.name;

        if (file.size > MAX_SPLASH_SIZE) {
            alert('Splash image size exceeds more than ' + (MAX_SPLASH_SIZE / 1024) + 'MB');
            return;
        }

        //setLinearLogoLoader(true);
        let error = "There was an error Uploading the Company Splash image!";

        try {
            let url = await getPresignedUrl('splash', fileName);
            if (url && url.success) {
                // Upload to presigned upload url
                let result = await uploadToPresignedUrl(
                    url.presignedUrl,
                    file
                );

                if (result && result.success) {
                    setCurrentCompany(
                        {...currentCompany, splashUrl: url.downloadUrl}
                    );
                    setCompany(
                        {...currentCompany, splashUrl: url.downloadUrl}
                    );
                    error = null;
                }
            }
        } catch (e) {
            error = e.toString();
        }

        if (error) {
            alert(error);
        }
    };

    /**
     * Function to remove the splash URL from the current company data.
     *
     * This function updates the current company and company objects by
     * setting the `splashUrl` property to an empty string. It effectively
     * clears any existing splash URL associated with the current company.
     *
     * No parameters or return values.
     */
    const removeSplashUrl = () => {
        setCurrentCompany(
            {...currentCompany, splashUrl: ''}
        );
        setCompany(
            {...currentCompany, splashUrl: ''}
        );
    };

    /**
     * This function handles the mouse enter event for the splash closer element.
     * It sets the splash hover state to true, typically to indicate an interactive
     * or hover state on the splash closer UI component.
     */
    const handleSplashCloserMouseEnter = () => {
        setSplashHover(true);
    };

    /**
     * A function to handle the mouse leave event on the splash closer element.
     * This function is invoked when the mouse pointer leaves the specified splash closer area
     * and it changes the state for splash hover to false.
     */
    const handleSplashCloserMouseLeave = () => {
        setSplashHover(false);
    };

    /**
     * A function to handle the mouse enter event on the logo closer element.
     * When triggered, it sets the hover state to true.
     */
    const handleLogoCloserMouseEnter = () => {
        setLogoHover(true);
    };

    /**
     * Handles the mouse leave event for the logo closer component.
     * This function sets the hover state to false, indicating that the mouse
     * has been moved away from the logo closer element.
     *
     * The function is typically used to reset any visual cues or interactivity
     * associated with hovering over the logo closer.
     */
    const handleLogoCloserMouseLeave = () => {
        setLogoHover(false);
    };

    /**
     * Handles the change event for updating the `firstName` property of the owner's information.
     * Updates the `currentCompany` and `company` states with the new owner's `firstName`
     * and recalculates the `email` property based on the updated `firstName`, the owner's
     * `lastName`, and the company's domain.
     *
     * @param {Object} evt - The event object triggered by the input change.
     * @param {Object} evt.currentTarget - The DOM element that triggered the event.
     * @param {string} evt.currentTarget.value - The current value entered in the input field.
     */
    const handleOwnerFirstNameChange = (evt) => {
        let value = evt.currentTarget.value,
            owner = {
                ...currentCompany.owner,
                firstName: value,
                email: value + '.' + currentCompany.owner.lastName + '@' +
                    currentCompany.domain
            };
        setCurrentCompany({
            ...currentCompany,
            owner: owner
        });
        setCompany({
            ...currentCompany,
            owner: owner
        });
    };

    /**
     * Handles the change event for updating the owner's last name.
     * Updates the owner's last name and email based on the input value,
     * and updates the current company and company state with the modified owner details.
     *
     * @param {Object} evt - The event object triggered by the change event.
     * @param {Object} evt.currentTarget - The element that triggered the event.
     * @param {string} evt.currentTarget.value - The new value of the owner's last name.
     */
    const handleOwnerLastNameChange = (evt) => {
        let value = evt.currentTarget.value,
            owner = {
                ...currentCompany.owner,
                lastName: value,
                email: currentCompany.owner.firstName + '.' + value + '@' +
                    currentCompany.domain
            };
        setCurrentCompany({
            ...currentCompany,
            owner: owner
        });
        setCompany({
            ...currentCompany,
            owner: owner
        });
    };

    /**
     * Handles the change event for the owner's email input field.
     * Updates the email property of the current company's owner object with the new value.
     * Sets the updated owner data in both `currentCompany` and `company` states.
     *
     * @param {Object} evt - The event object triggered by the email field change.
     * @param {Object} evt.currentTarget - The current target element that triggered the event.
     * @param {string} evt.currentTarget.value - The new value entered in the email input field.
     */
    const handleOwnerEmailChange = (evt) => {
        let value = evt.currentTarget.value,
            owner = {
                ...currentCompany.owner,
                email: value,
            };
        setCurrentCompany({
            ...currentCompany,
            owner: owner
        });
        setCompany({
            ...currentCompany,
            owner: owner
        });
    };

    /**
     * Handles the change of the owner's password input field by updating the
     * current company and owner state with the new password value. This function
     * is bound to an event listener of the input field and extracts the new
     * password from the event object.
     *
     * @param {Object} evt - The event object triggered by the input field change.
     */
    const handleOwnerPasswordChange = (evt) => {
        let value = evt.currentTarget.value,
            owner = {
                ...currentCompany.owner,
                password: value,
            };
        setCurrentCompany({
            ...currentCompany,
            owner: owner
        });
        setCompany({
            ...currentCompany,
            owner: owner
        });
    };

    /**
     * Function to handle tab change events.
     *
     * This function is triggered when a tab change occurs. It updates the current
     * tab to the newly selected value.
     *
     * @param {Object} evt - The event object triggered by the tab change action.
     * @param {any} newValue - The new value representing the selected tab.
     */
    const handleTabChange = (evt, newValue) => {
        setCurTab(newValue);
    };

    /**
     * Handles the addition of a new outsourcer to the current company.
     * Prompts the user to enter the new outsourcer's name and updates the current company's
     * outsourcers list.
     *
     * @param {Object} evt - The event object triggered by the action.
     */
    const handleAddOutsourcer = (/* evt */) => {
        let newValue = prompt('Enter new Outsourcer ');
        if (newValue) {
            // TODO - Call the API endpoint to add the outsourcer
            let newRow = {
                _id: null,
                company_id: currentCompany._id,
                name: newValue
            };
            let newOutsourcers = currentCompany.outsourcers.slice(0);
            newOutsourcers.push(newRow);
            setCurrentCompany({...currentCompany, outsourcers: newOutsourcers});
            setCompany({...currentCompany, outsourcers: newOutsourcers});
        }
    };

    /**
     * Handles the click event to edit an outsourcer's details.
     *
     * This function is triggered when an edit action is initiated for an outsourcer.
     * It prompts the user to enter a new value for the outsourcer's name, updates the
     * corresponding data row with the new value, and updates the current company's outsourcers
     * list.
     *
     * Note: The API endpoint to persist the updated outsourcer is not yet implemented.
     *
     * @param {Object} row - The data row of the outsourcer being edited. Contains details such as
     *     name, value, and ID.
     * @param {Event} evt - The event object representing the click event.
     */
    const handleEditOutsourcerClick = (row /* , evt */) => {
        let newValue = prompt('Enter new value for ' + row.name, row.value);
        if (newValue) {
            // TODO - Call the API endpoint to update the outsourcer
            let newRow = {...row, name: newValue};
            let newOutsourcers = currentCompany.outsourcers.map(ct => {
                if (ct._id === row._id) {
                    return newRow;
                }
                return ct;
            });
            setCurrentCompany({...currentCompany, outsourcers: newOutsourcers});
            setCompany({...currentCompany, outsourcers: newOutsourcers});
        }
    };

    /**
     * Handles the deletion of an outsourcer after user confirmation.
     *
     * @param {Object} row - The data object representing the outsourcer to be deleted.
     */
    const handleDeleteOutsourcerClick = (row) => {
        let proceed = window.confirm('Are you sure you want to delete this Outsourcer?');
        if (proceed) {
            // TODO - Call the API endpoint to delete the outsourcer
            let newOutsourcers = currentCompany.outsourcers.filter(ct => ct._id !== row._id);
            setCurrentCompany({...currentCompany, outsourcers: newOutsourcers});
            setCompany({...currentCompany, outsourcers: newOutsourcers});
        }
    };

    /**
     * Handles the addition of a new product to the current company.
     *
     * This function prompts the user to input a new product name. If the user provides a name,
     * a new product object is created and appended to the current company's product list.
     * The updated company object is then set as the current state.
     *
     * @param {Event} evt - The event object triggered by the user interaction.
     */
    /* const handleAddProduct = (evt) => {
        let newValue = prompt('Enter new Product');
        if (newValue) {
            let newRow = {
                _id: null,
                company_id: currentCompany._id,
                name: newValue
            };
            let newProducts = currentCompany.products.slice(0);
            newProducts.push(newRow);
            setCurrentCompany({...currentCompany, products: newProducts});
            setCompany({...currentCompany, products: newProducts});
        }
    }; */

    /**
     * Handles the edit action for a product row in a table or list.
     * Allows updating the product's name through a prompt and updates the state accordingly.
     *
     * @param {Object} row - The product row object to modify.
     * @param {string} row._id - The unique identifier of the product.
     * @param {string} row.name - The current name of the product.
     * @param {any} row.value - Additional value associated with the product.
     *
     * The function prompts the user to enter a new value for the product's name.
     * If a new value is provided, it updates the product object and reflects the
     * changes in the state of the associated company and its products.
     *
     * Note: The actual implementation for calling the API to persist the updates is not yet
     *     provided.
     */
    /* const handleEditProductClick = (row) => {
        let newValue = prompt('Enter new value for ' + row.name, row.value);
        if (newValue) {
            let newRow = {...row, name: newValue};
            let newProducts = currentCompany.products.map(ct => {
                if (ct._id === row._id) {
                    return newRow;
                }
                return ct;
            });
            setCurrentCompany({...currentCompany, products: newProducts});
            setCompany({...currentCompany, products: newProducts});
        }
    }; */

    /**
     * Handles the deletion of a product when the user clicks the delete button.
     *
     * Prompts the user with a confirmation dialog to ensure they want to proceed with the
     * deletion.
     * If the user confirms, filters out the specified product from the company's product list
     * and updates the state with the modified list.
     *
     * @param {Object} row - The product object to be deleted. Contains the product's details,
     *     including its unique identifier.
     */
    /* const handleDeleteProductClick = (row) => {
        let proceed = window.confirm('Are you sure you want to delete this Product?');
        if (proceed) {
            let newProducts = currentCompany.products.filter(ct => ct._id !== row._id);
            setCurrentCompany({...currentCompany, callTypes: newProducts});
            setCompany({...currentCompany, callTypes: newProducts});
        }
    }; */

    /**
     * Handles the addition of a new call type.
     * This function prompts the user to enter a new call type name, constructs a new call type
     * object, and updates the state with the new call type. Integration with an API endpoint to
     * persist the new call type is pending.
     *
     * @param {Object} evt - The event object associated with the user interaction that triggers
     *     this function.
     */
    const handleAddCallType = (/* evt */) => {
        let newValue = prompt('Enter new Call Type ');
        if (newValue) {
            let newRow = {
                _id: null,
                company_id: currentCompany._id,
                name: newValue
            };
            let newCallTypes = currentCompany.callTypes.slice(0);
            newCallTypes.push(newRow);
            setCurrentCompany({...currentCompany, callTypes: newCallTypes});
            setCompany({...currentCompany, callTypes: newCallTypes});
        }
    };

    /**
     * Handles the action of editing a call type by prompting the user for a new value
     * and updating the corresponding data in the call types list.
     *
     * @param {Object} row - The current call type object to be edited. Must include `name`,
     *     `value`, and `_id` properties.
     */
    const handleEditCallTypeClick = (row) => {
        let newValue = prompt('Enter new value for ' + row.name, row.value);
        if (newValue) {
            // TODO - Call the API endpoint to update the outsourcer
            let newRow = {...row, name: newValue};
            let newCallTypes = currentCompany.callTypes.map(ct => {
                if (ct._id === row._id) {
                    return newRow;
                }
                return ct;
            });
            setCurrentCompany({...currentCompany, callTypes: newCallTypes});
            setCompany({...currentCompany, callTypes: newCallTypes});
        }
    };

    /**
     * Handles the change event for the "active" status of a company.
     *
     * This callback function is triggered whenever the "active" state changes. It updates
     * the local state and manages the "active" status for the current company.
     *
     * @async
     * @function
     * @param {Event} evt - The event object that contains information about the change event.
     *                      The target property of the event is used to access the updated state.
     */
    const onIsActiveChange = async (evt) => {
        let src = evt.target;

        setCurrentCompany({...currentCompany, active: src.checked});
        setCompany({...currentCompany, active: src.checked});
        //setIsActiveCompany(src.checked);
    }

    /**
     * Handles the click event for deleting a call type.
     * Prompts the user with a confirmation dialog to confirm the deletion.
     * If the user confirms, the call type is removed from the company's list of call types.
     *
     * @param {Object} row - The call type object to be deleted, containing its details.
     */
    const handleDeleteCallTypeClick = (row) => {
        let proceed = window.confirm('Are you sure you want to delete this Call Type?');
        if (proceed) {
            // TODO - Call the API endpoint to delete the call type
            let newCallTypes = currentCompany.callTypes.filter(ct => ct._id !== row._id);
            setCurrentCompany({...currentCompany, callTypes: newCallTypes});
            setCompany({...currentCompany, callTypes: newCallTypes});
        }
    };

    let tabIndex = 0;
    return (
        <Dialog
            className={classes.modal}
            open={isModalVisible}
            onClose={handleClose}
            aria-labelledby="form-dialog-title"
            fullWidth
            maxWidth="lg"
            scroll="paper"
        >
            <DialogTitle className={classes.label} id="form-dialog-title">
                {currentCompany?.name ? `Edit Company ${currentCompany.name}` : 'Add Company'}
            </DialogTitle>
            <hr className={classes.hr}/>
            <DialogContent className={classes.modalContent}>
                <TabContext value={''+curTab}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs onChange={handleTabChange} value={''+curTab}>
                            <Tab value={'0'} label="General Info"/>
                            <Tab value={'1'} label="Account Owner"/>
                            {/*<Tab value={'2'} label="Products"/>*/}
                            <Tab value={'2'} label="Call Types"/>
                            <Tab value={'3'} label="Outsourcers"/>
                        </Tabs>
                    </Box>

                    <TabPanel value={''+tabIndex} index={tabIndex++}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControlLabel label="Is Active"
                                                  control={<Switch checked={isActiveCompany}
                                                                   value={1}
                                                                   name="isActive"
                                                                   id="isActive"
                                                                   onClick={onIsActiveChange}
                                                  />}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <InputLabel className={classes.label}
                                            htmlFor="name"
                                >
                                    Company Name
                                </InputLabel>
                                <TextField id="name"
                                           name="name"
                                           variant="outlined"
                                           fullWidth
                                           required
                                           autoFocus
                                           value={currentCompany?.name ?? ''}
                                           onChange={handleNameChange}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <InputLabel className={classes.label}
                                            htmlFor="domain"
                                >
                                    Company Domain
                                </InputLabel>
                                <TextField id="domain"
                                           name="domain"
                                           variant="outlined"
                                           fullWidth
                                           required
                                           autoFocus
                                           value={currentCompany?.domain?.replace('.ilearncc.com', '') ?? ''}
                                           onChange={handleDomainChange}
                                           style={{maxWidth: '85%'}}
                                />
                                <TextField disabled={true}
                                           aria-readonly={true}
                                           value=".ilearncc.com"
                                           style={{maxWidth: '14%'}}
                               />
                            </Grid>

                            <Grid item xs={12}>
                                <InputLabel className={classes.label}
                                            htmlFor="zonkerUrl"
                                >
                                    Send to Snowfly URL
                                </InputLabel>
                                <TextField id="zonkerUrl"
                                           name="zonkerUrl"
                                           variant="outlined"
                                           fullWidth
                                           required
                                           autoFocus
                                           value={currentCompany?.zonkerUrl}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <InputLabel className={classes.label}
                                            htmlFor="zonkerKey"
                                >
                                    Send to Snowfly API Key
                                </InputLabel>
                                <TextField id="zonkerKey"
                                           name="zonkerKey"
                                           variant="outlined"
                                           fullWidth
                                           required
                                           autoFocus
                                           value={currentCompany?.zonkerKey}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <InputLabel className={classes.label}
                                            htmlFor="zonkerSecret"
                                >
                                    Send to Snowfly API Secret
                                </InputLabel>
                                <TextField id="zonkerSecret"
                                           name="zonkerSecret"
                                           variant="outlined"
                                           fullWidth
                                           required
                                           autoFocus
                                           value={currentCompany?.zonkerSecret}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <InputLabel htmlFor="logoPicker" className={classes.inputLabel} style={{marginBottom: 10}}>
                                    Logo image
                                </InputLabel>
                                <Grid item xs={12} className={classes.companyImageField}>
                                    {currentCompany?.logoUrl && (
                                        <Grid item xs={10}>
                                            <div className={classes.logoCloser}
                                                 onMouseEnter={handleLogoCloserMouseEnter}
                                                 onMouseLeave={handleLogoCloserMouseLeave}
                                                 style={{width: 48}}
                                            >
                                                <CloseIcon
                                                    onClick={removeLogoUrl}
                                                    className={logoHover ? classes.closeIconHover : classes.closeIcon}
                                                    fontSize="small"
                                                />
                                            </div>
                                            <img src={currentCompany?.logoUrl}
                                                 alt="logo"
                                                 style={{width: 48, height: 'auto'}}
                                            />
                                        </Grid>
                                    )}
                                    <Button
                                        color="primary"
                                        component="label"
                                        variant="outlined"
                                        className={classes.linear}
                                        id="logoPicker"
                                    >
                                        Browse
                                        {' '}
                                        <input
                                            hidden
                                            type="file"
                                            accept="image/*"
                                            onChange={handleLogoChange}
                                            name="logo"
                                        />
                                    </Button>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <InputLabel htmlFor="title" className={classes.inputLabel} style={{marginBottom: 10}}>
                                    Splash image
                                </InputLabel>
                                <Grid item xs={12} className={classes.companyImageField}>
                                    {currentCompany?.splashUrl && (
                                        <Grid item xs={10}>
                                            <div className={classes.logoCloser}
                                                 onMouseEnter={handleSplashCloserMouseEnter}
                                                 onMouseLeave={handleSplashCloserMouseLeave}
                                                 style={{width: 256}}
                                            >
                                                <CloseIcon
                                                    onClick={removeSplashUrl}
                                                    className={splashHover ? classes.closeIconHover : classes.closeIcon}
                                                    fontSize="small"
                                                />
                                            </div>
                                            <img src={currentCompany?.splashUrl}
                                                 alt="splash"
                                                 style={{width: 256, height: 'auto'}}
                                            />
                                        </Grid>
                                    )}
                                    <Button
                                        color="primary"
                                        component="label"
                                        variant="outlined"
                                        className={classes.linear}
                                    >
                                        Browse
                                        {' '}
                                        <input
                                            hidden
                                            type="file"
                                            accept="image/*"
                                            onChange={handleSplashChange}
                                            name="splash"
                                        />
                                    </Button>
                                </Grid>
                            </Grid>
                        </Grid>
                    </TabPanel>

                    <TabPanel value={''+tabIndex} index={tabIndex++}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <InputLabel className={classes.label}
                                            htmlFor="firstName"
                                >
                                    First Name
                                </InputLabel>
                                <TextField id="firstName"
                                           name="firstName"
                                           variant="outlined"
                                           fullWidth
                                           required
                                           autoFocus
                                           value={currentCompany?.owner?.firstName ?? ''}
                                           onChange={handleOwnerFirstNameChange}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <InputLabel className={classes.label}
                                            htmlFor="lastName"
                                >
                                    Last Name
                                </InputLabel>
                                <TextField id="lastName"
                                           name="lastName"
                                           variant="outlined"
                                           fullWidth
                                           required
                                           autoFocus
                                           value={currentCompany?.owner?.lastName ?? ''}
                                           onChange={handleOwnerLastNameChange}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <InputLabel className={classes.label}
                                            htmlFor="email"
                                >
                                    Email Address
                                </InputLabel>
                                <TextField id="email"
                                           name="email"
                                           variant="outlined"
                                           fullWidth
                                           required
                                           autoFocus
                                           value={currentCompany?.owner?.email ?? ''}
                                           onChange={handleOwnerEmailChange}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <InputLabel className={classes.label}
                                            htmlFor="password"
                                >
                                    Password
                                </InputLabel>
                                <TextField id="password"
                                           name="password"
                                           variant="outlined"
                                           type="password"
                                           fullWidth
                                           required
                                           autoFocus
                                           value={currentCompany?.owner?.password ?? ''}
                                           onChange={handleOwnerPasswordChange}
                                />
                            </Grid>
                        </Grid>
                    </TabPanel>

                    {/*<TabPanel value={tabIndex} index={tabIndex++}>
                        <TableContainer component={Paper}>
                            <Table aria-label="Company Products Table" className={classes.root}>
                                <TableHead className={classes.thead}>
                                    <TableRow className={classes.tableRow}>
                                        <TableCell className={classes.headingAuto}>
                                            Name
                                        </TableCell>
                                        <TableCell className={classes.actions}>
                                            <Tooltip title="Add a new Product"
                                                     placement="bottom"
                                            >
                                                <span>
                                                    <Button variant="contained"
                                                            size="small"
                                                            className={classes.addButton}
                                                            disableElevation
                                                    >
                                                        <AddIcon onClick={handleAddProduct}
                                                                 fontSize="small"
                                                        />
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {currentCompany?.products?.length > 0 && currentCompany?.products?.map((row) => (
                                        <TableRow
                                            key={row._id}
                                        >
                                            <TableCell className={classes.bold}>
                                                {row.name}
                                            </TableCell>
                                            <TableCell className={classes.actions}>
                                                <Tooltip
                                                    title="Edit this Product"
                                                    placement="bottom"
                                                >
                                                    <span>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            className={classes.buttonSqr}
                                                            disableElevation
                                                        >
                                                          <EditIcon
                                                              onClick={() => handleEditProductClick(row)}
                                                              fontSize="small"
                                                          />
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip
                                                    title="Delete this Product"
                                                    placement="bottom"
                                                >
                                                    <span>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            className={classes.buttonDelete}
                                                            disableElevation
                                                        >
                                                          <DeleteIcon
                                                              onClick={() => handleDeleteProductClick(row)}
                                                              fontSize="small"
                                                          />
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </TabPanel>*/}

                    <TabPanel value={''+tabIndex} index={tabIndex++}>
                        <TableContainer component={Paper}>
                            <Table aria-label="Company Call Types Table" className={classes.root}>
                                <TableHead className={classes.thead}>
                                    <TableRow className={classes.tableRow}>
                                        <TableCell className={classes.headingAuto}>
                                            Type
                                        </TableCell>
                                        <TableCell className={classes.actions}>
                                            <Tooltip title="Add a new Call Type"
                                                     placement="bottom"
                                            >
                                                <span>
                                                    <Button variant="contained"
                                                            size="small"
                                                            className={classes.addButton}
                                                            disableElevation
                                                    >
                                                        <AddIcon onClick={handleAddCallType}
                                                                 fontSize="small"
                                                        />
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {currentCompany?.callTypes?.length > 0 && currentCompany?.callTypes?.map((row) => (
                                        <TableRow
                                            key={row._id}
                                        >
                                            <TableCell className={classes.bold}>
                                                {row.name}
                                            </TableCell>
                                            <TableCell className={classes.actions}>
                                                <Tooltip
                                                    title="Edit this Call Type"
                                                    placement="bottom"
                                                >
                                                    <span>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            className={classes.buttonSqr}
                                                            disableElevation
                                                        >
                                                          <EditIcon
                                                              onClick={() => handleEditCallTypeClick(row)}
                                                              fontSize="small"
                                                          />
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip
                                                    title="Delete this Call Type"
                                                    placement="bottom"
                                                >
                                                    <span>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            className={classes.buttonDelete}
                                                            disableElevation
                                                        >
                                                          <DeleteIcon
                                                              onClick={() => handleDeleteCallTypeClick(row)}
                                                              fontSize="small"
                                                          />
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </TabPanel>

                    <TabPanel value={''+tabIndex} index={tabIndex++}>
                        <TableContainer component={Paper}>
                            <Table aria-label="Company Outsourcers Table" className={classes.root}>
                                <TableHead className={classes.thead}>
                                    <TableRow className={classes.tableRow}>
                                        <TableCell className={classes.headingAuto}>
                                            Name
                                        </TableCell>
                                        <TableCell className={classes.actions}>
                                            <Tooltip title="Add a new Outsourcer"
                                                     placement="bottom"
                                            >
                                                <span>
                                                    <Button variant="contained"
                                                            size="small"
                                                            className={classes.addButton}
                                                            disableElevation
                                                    >
                                                        <AddIcon onClick={handleAddOutsourcer}
                                                                 fontSize="small"
                                                        />
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {currentCompany?.outsourcers?.length > 0 && currentCompany?.outsourcers?.map((row) => (
                                        <TableRow
                                            key={row._id}
                                        >
                                            <TableCell className={classes.bold}>
                                                {row.name}
                                            </TableCell>
                                            <TableCell className={classes.actions}>
                                                <Tooltip
                                                    title="Edit this Outsourcer"
                                                    placement="bottom"
                                                >
                                                    <span>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            className={classes.buttonSqr}
                                                            disableElevation
                                                        >
                                                          <EditIcon
                                                              onClick={(evt) => handleEditOutsourcerClick(row, evt)}
                                                              fontSize="small"
                                                          />
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip
                                                    title="Delete this Outsourcer"
                                                    placement="bottom"
                                                >
                                                    <span>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            className={classes.buttonDelete}
                                                            disableElevation
                                                        >
                                                          <DeleteIcon
                                                              onClick={(evt) => handleDeleteOutsourcerClick(row, evt)}
                                                              fontSize="small"
                                                          />
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </TabPanel>
                </TabContext>

            </DialogContent>

            <DialogActions>
                <Button onClick={doCloseDialog}
                        id='cancelButton'
                        color='secondary'
                        variant='contained'
                >
                    Cancel
                </Button>
                <Button onClick={handleSave}
                        id='sendButton'
                        color='primary'
                        variant='contained'
                >
                    Send
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/**
 * PropTypes for the EditCompanyModal component.
 * This defines the expected props that the component should receive
 * to ensure proper functionality and to catch type errors during development.
 *
 * @prop {bool} isOpen - Determines whether the modal is visible or not.
 * @prop {func} onClose - Callback function to handle the closing of the modal.
 * @prop {object} companyData - Data object containing details about the company being edited.
 * @prop {func} onSubmit - Callback function invoked when the form is submitted.
 * @prop {bool} isLoading - Indicates whether the modal is in a loading state.
 * @prop {object} errors - An object containing form validation error messages.
 */
EditCompanyModal.propTypes = {
    company: PropTypes.object,
    visible: PropTypes.bool,
    onClose: PropTypes.func,
    setCompany: PropTypes.func,
    onSave: PropTypes.func,
    classes: PropTypes.object,
};

export default connect(null)(EditCompanyModal);
